import React, { FC } from 'react';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';

interface AzurePaginationProps {
  currentPage: number;
  onPageChange: (page: number) => void;
  totalItems: number;
  itemsPerPage: number;
  hasNextPage: boolean;
  isLoading?: boolean;
  embedded?: boolean;
}

const AzurePagination: FC<AzurePaginationProps> = ({
  currentPage,
  onPageChange,
  totalItems,
  itemsPerPage,
  hasNextPage,
  isLoading = false,
  embedded = false,
}) => {
  const startItem = (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const getPageNumbers = () => {
    const pages = [];
    
    // For skipToken pagination, we show a simplified pagination
    // Always show current page in the center
    if (currentPage > 1) {
      pages.push(1);
    }
    
    if (currentPage > 3) {
      pages.push('...');
    }
    
    if (currentPage > 2) {
      pages.push(currentPage - 1);
    }
    
    pages.push(currentPage);
    
    if (hasNextPage) {
      pages.push(currentPage + 1);
    }
    
    if (hasNextPage && currentPage + 1 < totalPages) {
      pages.push('...');
    }
    
    return pages;
  };

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-4 ${embedded ? 'mt-0' : 'mt-4'}`}>
      <div className="text-sm text-slate-600 order-2 sm:order-1">
        Showing {startItem} to {endItem} of {totalItems} results
      </div>
      
      <div className="flex items-center space-x-1 sm:space-x-2 order-1 sm:order-2">
        {/* Previous button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1 || isLoading}
          className={`flex items-center px-2 py-1.5 text-sm font-medium rounded-full border transition-all duration-200 ${
            currentPage === 1 || isLoading
              ? 'border-gray-200 text-gray-400 cursor-not-allowed'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
          }`}
        >
          {FiChevronLeft({ size: 18, className: "" })}
        </button>

        {/* Page numbers */}
        <div className="flex items-center space-x-1">
          {getPageNumbers().map((page, index) => (
            <button
              key={index}
              onClick={() => typeof page === 'number' && onPageChange(page)}
              disabled={page === '...'}
              className={`px-3 py-1 text-[15px] font-medium rounded-full border transition-all duration-200 ${
                page === currentPage
                  ? 'bg-[#086DE1] text-white border-[#086DE1] shadow-sm'
                  : page === '...'
                  ? 'border-transparent text-black cursor-default'
                  : 'border-gray-300 text-black hover:bg-gray-50 hover:border-gray-400'
              }`}
            >
              {page}
            </button>
          ))}
        </div>

        {/* Next button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!hasNextPage || isLoading}
          className={`flex items-center px-2 py-1.5 text-sm font-medium rounded-full border transition-all duration-200 ${
            !hasNextPage || isLoading
              ? 'border-gray-200 text-gray-400 cursor-not-allowed'
              : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
          }`}
        >
          {FiChevronRight({ size: 18 })}
        </button>
      </div>
    </div>
  );
};

export default AzurePagination;
